import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, RotateCcw, Award } from 'lucide-react';

export interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

interface ModuleQuizProps {
  questions: QuizQuestion[];
  moduleKey: string;
  courseKey: string;
  passingScore?: number;
  isFinalExam?: boolean;
  examTitle?: string;
  certificationName?: string;
  onComplete?: (passed: boolean, score: number) => void;
}

const ModuleQuiz: React.FC<ModuleQuizProps> = ({
  questions,
  moduleKey,
  courseKey,
  passingScore = 60,
  isFinalExam = false,
  examTitle,
  certificationName,
  onComplete,
}) => {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  const storageKey = `ic-course-${courseKey}-${moduleKey}`;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedScore = localStorage.getItem(`${storageKey}-score`);
      if (savedScore) {
        setScore(parseInt(savedScore));
        setSubmitted(true);
      }
    }
  }, [storageKey]);

  const handleSelect = (questionIndex: number, optionIndex: number) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [questionIndex]: optionIndex }));
  };

  const handleSubmit = () => {
    let correct = 0;
    questions.forEach((q, i) => {
      if (answers[i] === q.correctIndex) correct++;
    });
    const pct = Math.round((correct / questions.length) * 100);
    setScore(pct);
    setSubmitted(true);

    if (typeof window !== 'undefined') {
      localStorage.setItem(`${storageKey}-score`, String(pct));
      if (pct >= passingScore) {
        localStorage.setItem(`${storageKey}-complete`, 'true');
      }
    }

    onComplete?.(pct >= passingScore, pct);
  };

  const handleRetake = () => {
    setAnswers({});
    setSubmitted(false);
    setScore(0);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(`${storageKey}-score`);
      localStorage.removeItem(`${storageKey}-complete`);
    }
  };

  const allAnswered = Object.keys(answers).length === questions.length;
  const passed = score >= passingScore;

  return (
    <div className="mt-12 mb-8">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-primary-900 px-6 py-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Award className="w-5 h-5 text-accent-400" />
            {isFinalExam
              ? examTitle || 'Final Qualification Exam'
              : 'Module Quiz'}
          </h3>
          <p className="text-primary-300 text-sm mt-1">
            {questions.length} questions — Pass mark: {passingScore}%
          </p>
        </div>

        {/* Score banner */}
        {submitted && (
          <div
            className={`px-6 py-4 flex items-center justify-between ${
              passed ? 'bg-green-50 border-b border-green-200' : 'bg-red-50 border-b border-red-200'
            }`}
          >
            <div className="flex items-center gap-3">
              {passed ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600" />
              )}
              <div>
                <p className={`font-bold ${passed ? 'text-green-800' : 'text-red-800'}`}>
                  Score: {score}%
                </p>
                <p className={`text-sm ${passed ? 'text-green-600' : 'text-red-600'}`}>
                  {passed
                    ? isFinalExam
                      ? `Congratulations! You have earned your ${certificationName || 'certification'}.`
                      : 'Module complete! You can proceed to the next module.'
                    : `You need ${passingScore}% to pass. Review the material and try again.`}
                </p>
              </div>
            </div>
            <button
              onClick={handleRetake}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-md hover:bg-white/50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Retake
            </button>
          </div>
        )}

        {/* Questions */}
        <div className="divide-y divide-gray-100">
          {questions.map((q, qi) => {
            const isCorrect = submitted && answers[qi] === q.correctIndex;
            const isWrong = submitted && answers[qi] !== undefined && answers[qi] !== q.correctIndex;

            return (
              <div key={qi} className="px-6 py-5">
                <p className="font-semibold text-primary-900 mb-3">
                  <span className="text-accent-500 mr-2">Q{qi + 1}.</span>
                  {q.question}
                </p>

                <div className="space-y-2 ml-1">
                  {q.options.map((opt, oi) => {
                    const isSelected = answers[qi] === oi;
                    const isThisCorrect = submitted && oi === q.correctIndex;

                    let optionClass = 'border-gray-200 hover:border-accent-400 hover:bg-accent-50/30';
                    if (submitted) {
                      if (isThisCorrect) optionClass = 'border-green-400 bg-green-50';
                      else if (isSelected && !isThisCorrect) optionClass = 'border-red-400 bg-red-50';
                      else optionClass = 'border-gray-100 opacity-60';
                    } else if (isSelected) {
                      optionClass = 'border-accent-400 bg-accent-50/50';
                    }

                    return (
                      <label
                        key={oi}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${optionClass} ${submitted ? 'cursor-default' : ''}`}
                      >
                        <input
                          type="radio"
                          name={`quiz-${moduleKey}-q${qi}`}
                          checked={isSelected}
                          onChange={() => handleSelect(qi, oi)}
                          disabled={submitted}
                          className="accent-accent-500 w-4 h-4"
                        />
                        <span className="text-sm text-gray-800">{opt}</span>
                        {submitted && isThisCorrect && (
                          <CheckCircle className="w-4 h-4 text-green-600 ml-auto flex-shrink-0" />
                        )}
                        {submitted && isSelected && !isThisCorrect && (
                          <XCircle className="w-4 h-4 text-red-500 ml-auto flex-shrink-0" />
                        )}
                      </label>
                    );
                  })}
                </div>

                {submitted && (
                  <p className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-md px-3 py-2">
                    <span className="font-medium">Explanation:</span> {q.explanation}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Submit */}
        {!submitted && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
            <button
              onClick={handleSubmit}
              disabled={!allAnswered}
              className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                allAnswered
                  ? 'bg-accent-500 text-white hover:bg-accent-600 shadow-sm'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Submit Answers ({Object.keys(answers).length}/{questions.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleQuiz;
